import {isUserDesigner} from '@Utils/user.util';
import {getUserType} from '@Libraries/user.library';
import {openMessageModal} from '@Modals/message-modal';
import type {UserImageVOResponse} from '@Libraries/user-image-library';
import type {PollKickoffAck} from '@Libraries/pollable-job.library.types';

type StockStatusStorage = Record<SOURCE, boolean>;
let stockStatus: StockStatusStorage | null = null;

export enum SOURCE {
  GETTY = 'getty',
  FACEBOOK = 'facebook',
  PIXABAY = 'pixabay',
  STORYBLOCKS = 'storyblocks',
  STORYBLOCKS_ILLUSTRATIONS = 'storyblocks-illustrations',
  PIXABAY_ILLUSTRATIONS = 'pixabay-illustrations',
  GETTY_ILLUSTRATIONS = 'getty-illustrations',
  PMW = 'pmw',
  PMW_SHAPES = 'pmw-shapes',
  PMW_STICKERS = 'pmw-sticker',
  PMW_ICONS = 'pmw-icon',
  UPLOAD = 'upload',
  AI_TEXT_TO_IMAGE = 'AI_TEXT_TO_IMAGE',
  PMW_IMAGE = 'pmw-image',
}

export const MAX_GETTY_VIDEO_ITEMS = 1;

export const initStockStatus = async () => {
  if (!stockStatus) {
    stockStatus = await window.PMW.readLocal('stockmedia/getStockStatus');
  }
};

export const updateStockSourceAvailability = (source: SOURCE, isAvailable: boolean): void => {
  if (!stockStatus) {
    return;
  }
  stockStatus[source] = isAvailable;
};

/**
 * You need to call initStockStatus before this function
 * @param source
 * @return {boolean}
 */
export const isStockSourceAvailable = (source: SOURCE) => {
  if (!stockStatus) {
    return true;
  }

  return stockStatus[source] ?? true;
};

/**
 * @param source
 * @return {string|*}
 */
export const getStockSourceUnavailableTitle = (source: SOURCE) => {
  if (isStockSourceAvailable(source)) {
    return '';
  }

  switch (source) {
    case SOURCE.STORYBLOCKS:
      return window.i18next.t('pmwjs_error_communicating_with_storyblocks');

    default:
      return window.i18next.t('pmwjs_error_communicating_with_media_stock_source');
  }
};

export const getAllStockSources = (): Array<SOURCE> => {
  const sources = [SOURCE.GETTY, SOURCE.STORYBLOCKS];
  if (!isUserDesigner(getUserType())) {
    sources.push(SOURCE.PIXABAY);
  }
  return sources;
};

export const isStoryblockAvailable = async () => {
  await initStockStatus();
  return stockStatus !== null ? stockStatus[SOURCE.STORYBLOCKS] : true;
};

export const isGettyAvailable = async () => {
  await initStockStatus();
  return stockStatus !== null ? stockStatus[SOURCE.GETTY] : true;
};

export const isPixabayAvailable = async () => {
  await initStockStatus();
  return stockStatus !== null ? stockStatus[SOURCE.PIXABAY] : true;
};

export interface GettyImageUploadProps {
  type: string;
  imageid: string;
  title: string;
  artist: string;
  collection: number;
}

/**
 * Sends data of image to be imported into our system from Getty Images
 * @param {object} p Parameters to send to server
 * @return {Promise<*>}
 * @throws Error
 */
export function gettyImageUpload(p: GettyImageUploadProps): Promise<UserImageVOResponse> {
  return window.PMW.writeLocal('posterimage/gettyupload', p) as Promise<UserImageVOResponse>;
}

export interface PixabyImageUploadProps {
  url: string;
}

/**
 * Sends data of image to be imported into our system from Pixabay
 * @param {object} p Parameters to send to server
 * @return {Promise<*>}
 * @throws Error
 */
export function pixabayImageUpload(p: PixabyImageUploadProps): Promise<UserImageVOResponse> {
  return window.PMW.writeLocal('posterimage/pixabayupload', p) as Promise<UserImageVOResponse>;
}

export interface StoryblockImageUploadProps {
  userid?: number;
  imageid: string;
  format: string;
}

/**
 * Sends data of image (photos) to be imported into our system from StoryBlocks
 * @param {object} p Parameters to send to server
 * @return {Promise<*>}
 * @throws Error
 */
export function storyBlocksImageUpload(p: StoryblockImageUploadProps): Promise<UserImageVOResponse> {
  return window.PMW.writeLocal('posterimage/storyBlocksImageUpload', p) as Promise<UserImageVOResponse>;
}

/**
 Sends data of image (clipart/vectors/illustrations) to be imported into our system from StoryBlocks
 * @param {object} p Parameters to send to server
 * @return {Promise<*>}
 * @throws Error
 */
export function storyBlocksIllustrationsUpload(p: StoryblockImageUploadProps): Promise<UserImageVOResponse> {
  return window.PMW.writeLocal('posterimage/storyBlocksIllustrationsUpload', p) as Promise<UserImageVOResponse>;
}

/**
 * Sends data of vectors to be imported into our system from Pixabay
 * @param {object} p Parameters to send to server
 */
export function pixabayIllustrationsUpload(p: PixabyImageUploadProps): Promise<UserImageVOResponse> {
  return window.PMW.writeLocal('posterimage/pixabayIllustrationUpload', p) as Promise<UserImageVOResponse>;
}

export interface GettyVideoUploadProps {
  videoId: string;
  title: string;
  artist: string;
  collection: number;
}

/**
 * Kicks off importing a Getty Images video into the user's account through the pollableJob V2
 * transport. title/artist/collection are the Getty metadata the upload pipeline persists into the
 * getty_video table, so they must be sent alongside videoId just as the legacy gettyUpload did.
 * @param {object} p Parameters to send to server
 * @return {Promise<PollKickoffAck>}
 * @throws Error
 */
export function gettyVideoUploadV2(p: GettyVideoUploadProps): Promise<PollKickoffAck> {
  return window.PMW.writeLocal('postervideo/gettyUploadV2', p) as Promise<PollKickoffAck>;
}

export interface PixabyVideoUploadProps {
  videoId: string;
  videoHighRes: string;
  videoSmallRes: string;
}

/**
 * Kicks off importing a Pixabay video into the user's account through the pollableJob V2
 * transport.
 * @param {object} p Parameters to send to server
 * @return {Promise<PollKickoffAck>}
 * @throws Error
 */
export function pixabayVideoUploadV2(p: PixabyVideoUploadProps): Promise<PollKickoffAck> {
  return window.PMW.writeLocal('postervideo/pixabayUploadV2', p) as Promise<PollKickoffAck>;
}

export interface StoryblockVideoUploadProps {
  videoId: string;
}

/**
 * Kicks off importing a StoryBlocks video into the user's account through the pollableJob V2
 * transport.
 * @param {object} p Parameters to send to server
 * @return {Promise<PollKickoffAck>}
 * @throws Error
 */
export function storyBlocksVideoUploadV2(p: StoryblockVideoUploadProps): Promise<PollKickoffAck> {
  return window.PMW.writeLocal('postervideo/videoBlocksVideoUploadV2', p) as Promise<PollKickoffAck>;
}

export interface StoryblockAudioUploadProps {
  audioId: string;
  title: string;
}

/**
 * Kicks off importing a StoryBlocks audio track into the user's account through the pollableJob
 * V2 transport.
 * @param {object} p Parameters to send to server
 * @return {Promise<PollKickoffAck>}
 * @throws Error
 */
export function storyBlocksAudioUploadV2(p: StoryblockAudioUploadProps): Promise<PollKickoffAck> {
  return window.PMW.writeLocal('posteraudio/storyBlocksAudioUploadV2', p) as Promise<PollKickoffAck>;
}

export interface GettyImageUploadV2Props {
  imageId: string;
  type: string;
  title: string;
  artist: string;
  collection: number;
  tempUploadingImageUID?: string;
}

/**
 * Kicks off importing a Getty Images image into the user's account through the pollableJob V2
 * transport. type/title/artist/collection are the Getty metadata the upload pipeline persists,
 * so they must be sent alongside imageId just as the legacy gettyImageUpload did.
 * @param {object} p Parameters to send to server
 * @return {Promise<PollKickoffAck>}
 * @throws Error
 */
export function gettyImageUploadV2(p: GettyImageUploadV2Props): Promise<PollKickoffAck> {
  return window.PMW.writeLocal('posterimage/gettyUploadV2', p) as Promise<PollKickoffAck>;
}

export interface PixabayImageUploadV2Props {
  url: string;
  imageId?: string;
  tempUploadingImageUID?: string;
}

/**
 * Kicks off importing a Pixabay image into the user's account through the pollableJob V2
 * transport.
 * @param {object} p Parameters to send to server
 * @return {Promise<PollKickoffAck>}
 * @throws Error
 */
export function pixabayImageUploadV2(p: PixabayImageUploadV2Props): Promise<PollKickoffAck> {
  return window.PMW.writeLocal('posterimage/pixabayUploadV2', p) as Promise<PollKickoffAck>;
}

export interface StoryblockImageUploadV2Props {
  imageId: string;
  tempUploadingImageUID?: string;
}

/**
 * Kicks off importing a StoryBlocks image into the user's account through the pollableJob V2
 * transport.
 * @param {object} p Parameters to send to server
 * @return {Promise<PollKickoffAck>}
 * @throws Error
 */
export function storyBlocksImageUploadV2(p: StoryblockImageUploadV2Props): Promise<PollKickoffAck> {
  return window.PMW.writeLocal('posterimage/storyBlocksImageUploadV2', p) as Promise<PollKickoffAck>;
}

export function shouldOpenStockModal() {
  if (window.postermywall && !window.postermywall.core.model.AppProxy.isFeatureEnabled('addStockMultiMediaItem')) {
    openMessageModal({
      title: window.i18next.t('pmwjs_add_stock_image_disabled_title'),
      text: window.i18next.t('pmwjs_add_stock_image_disabled_message'),
    });
    return false;
  }
  return true;
}
